import { faSignOutAlt, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'gatsby-plugin-intl';
import React, { memo, FC, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { IntlShape } from 'react-intl';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Navbar,
  NavbarToggler,
  NavItem,
  UncontrolledDropdown,
} from 'reactstrap';
import LOGO from '../../../assets/images/copic_CMYK.svg';
import './Header.scss';
import { Msg } from '../../../utils/helpers/MsgHelper';
import { logout } from '../../../services/AuthService';
import HeaderProfile from './HeaderProfile';

/**
 * @interface HeaderInternalProps
 * @description Contains Header component props attributes and types
 */
interface HeaderInternalProps {
  /**
   * @method toggleResponsiveSideNav
   * @description To toggle mobile responsive Side navigation menu
   * @memberof HeaderInternalProps
   */
  toggleResponsiveSideNav: () => void;
  showResponsiveSideNav: boolean;
  /**
   * @field intl
   * @description Internationalization object
   * @type {IntlShape}
   * @memberof HeaderInternalProps
   */
  intl: IntlShape;
}

/**
 * @interface toggleResponsiveSideNav
 * @description Contains Header components props attributes and types
 */
interface HeaderExternalProps {
  /**
   * @method toggleResponsiveSideNav
   * @description To toggle mobile responsive Side navigation menu
   * @memberof HeaderExternalProps
   */
  toggleResponsiveSideNav: () => void;
  showResponsiveSideNav: boolean;
  /**
   * @field intl
   * @description Internationalization object
   * @type {IntlShape}
   * @memberof HeaderExternalProps
   */
  intl: IntlShape;
}

interface State {
  isOpen: boolean;
}

/**
 * @component Header
 * @description Renders header component containing with logo and language picker
 */
const Header: FC<HeaderInternalProps> = (props: HeaderInternalProps) => {
  const { showResponsiveSideNav, toggleResponsiveSideNav, intl } = props;
  const { keycloak } = useKeycloak();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleLogout = (): void => {
    logout();
    const logoutURL = window.location.origin;
    keycloak.logout({ redirectUri: logoutURL });
  };

  const updateIsOpen = (open: boolean): void => {
    setIsOpen(open);
    if (showResponsiveSideNav) {
      toggleResponsiveSideNav();
    }
  };

  return (
    <Navbar dark className='header-nav bg-white' fixed='top'>
      <NavbarToggler
        className='pl-0 pr-3'
        id='navigationBar'
        aria-label='Navigation Menu'
        onClick={toggleResponsiveSideNav}
      />
      <div className='navbar-brand logo-link'>
        <img src={LOGO} alt='logo' className='header-logo' />
      </div>

      <Nav horizontal='end' className='ml-lg-auto flex align-items-center'>
        <FontAwesomeIcon className='mt-1 icon-size header-item-display' icon={faUserCircle} size='2x' color='#00475c' />
        <UncontrolledDropdown
          className='header-item-search-display'
          toggle={() => updateIsOpen(!isOpen)}
          isOpen={isOpen}
        >
          <DropdownToggle>
            <FontAwesomeIcon className='mt-1 icon-size' icon={faUserCircle} size='2x' color='#00475c' />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem className='mt-0'>
              <span className='mr-3 ml-2'>
                <FontAwesomeIcon icon={faSignOutAlt} />
              </span>
              <Link to='/' onClick={handleLogout}>
                <span>{Msg(intl, 'navigationMenu.mainMenuHeader.link.logout')}</span>
              </Link>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
        <NavItem className='ml-2 header-item-display'>
          <HeaderProfile intl={intl} logoutHandler={handleLogout} />
        </NavItem>
      </Nav>
    </Navbar>
  );
};

const JSXInterface: React.FC<HeaderExternalProps> = () => <> </>;

export default memo(Header) as typeof JSXInterface;
